import { getCustomerIdFromToken } from '../../utils/getCustomerIdFromToken';
import { post } from '../apiService';
import { invalidateOfficeHoursListCache } from './postOfficeHoursList';

interface OfficeHourInput {
  dayOfWeek: number;
  open_time: string;
  close_time: string;
  closed: boolean;
}

export const createOfficeHours = async (
  practiceId: string,
  body: OfficeHourInput[],
): Promise<{ status: string }> => {
  const customer = getCustomerIdFromToken();
  if (!customer) throw new Error('Customer Id is not available');

  // Validate office hours before sending to API
  const validationErrors: string[] = [];
  body.forEach((hour: OfficeHourInput, index) => {
    if (!hour.closed) {
      // For open hours, validate time fields
      if (!hour.open_time || hour.open_time.trim() === '') {
        validationErrors.push(
          `Hour ${index + 1}: open_time is required for open hours`,
        );
      }
      if (!hour.close_time || hour.close_time.trim() === '') {
        validationErrors.push(
          `Hour ${index + 1}: close_time is required for open hours`,
        );
      }
      if (
        hour.open_time &&
        hour.close_time &&
        hour.open_time >= hour.close_time
      ) {
        validationErrors.push(
          `Hour ${index + 1}: open_time must be before close_time`,
        );
      }
    }
  });

  if (validationErrors.length > 0) {
    throw new Error(
      `Office hours validation failed:\n${validationErrors.join(
        '\n',
      )}`,
    );
  }

  const url = `/resourceMatrix/office_hour/create?customerId=${customer}`;
  try {
    const response = await post(url, {
      practiceId,
      officeHours: body,
    });
    invalidateOfficeHoursListCache();
    return response as { status: string };
  } catch (error) {
    throw new Error('Failed to create office hours');
  }
};

// New bulk update function for the updated API
export const updateOfficeHours = async (
  practiceId: string,
  officeHours: Array<{
    id: string;
    dayOfWeek: number;
    open_time: string;
    close_time: string;
    endOfDay: boolean | null;
    keepTakingCalls: string | null;
    closed: boolean;
  }>,
): Promise<{ status: string }> => {
  const customer = getCustomerIdFromToken();
  if (!customer) throw new Error('Customer Id is not available');

  // Validate office hours before sending to API
  const validationErrors: string[] = [];
  officeHours.forEach((hour, index) => {
    if (!hour.closed) {
      // For open hours, validate time fields
      if (!hour.open_time || hour.open_time.trim() === '') {
        validationErrors.push(
          `Hour ${index + 1}: open_time is required for open hours`,
        );
      }
      if (!hour.close_time || hour.close_time.trim() === '') {
        validationErrors.push(
          `Hour ${index + 1}: close_time is required for open hours`,
        );
      }
      if (
        hour.open_time &&
        hour.close_time &&
        hour.open_time >= hour.close_time
      ) {
        validationErrors.push(
          `Hour ${index + 1}: open_time must be before close_time`,
        );
      }
    }
  });

  if (validationErrors.length > 0) {
    throw new Error(
      `Office hours validation failed:\n${validationErrors.join(
        '\n',
      )}`,
    );
  }

  const url = `/resourceMatrix/office_hour/update?customerId=${customer}`;
  try {
    const response = await post(url, {
      practiceId,
      officeHours,
    });
    invalidateOfficeHoursListCache();
    return response as { status: string };
  } catch (error) {
    throw new Error('Failed to update office hours');
  }
};
